import { useState } from 'react';

import {
  FileImageOutlined,
  PictureOutlined,
  ZoomInOutlined,
} from '@ant-design/icons';
import { Image, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { TiendaPerfectaVan } from 'models/TiendaPerfecta';

import { VAN_STATUS_COLORS } from '../types';
import CrewSlot from './CrewSlot';

const I18N_BASE = 'scenes.vans.components.van_card';

/** Distribuidores visibles antes de agrupar el resto en un “+N”. */
const VISIBLE_DISTRIBUTORS = 2;

interface VanCardProps {
  van: TiendaPerfectaVan;
  onCrewClick: (operatorId: string) => void;
}

function VanCard({ van, onCrewClick }: VanCardProps) {
  const { t, i18n } = useTranslation();

  // `photoUrl` es una URL firmada que puede haber caducado o no resolver: si la
  // imagen no carga se cae al mismo placeholder que cuando no hay foto, en vez
  // de dejar el icono de imagen rota.
  const [photoFailed, setPhotoFailed] = useState(false);

  // El visor se controla desde acá para poder abrirlo también con el teclado:
  // la máscara de antd solo responde al click.
  const [previewOpen, setPreviewOpen] = useState(false);

  const distributors = van.distributors ?? [];
  const visible = distributors.slice(0, VISIBLE_DISTRIBUTORS);
  const extra = distributors.slice(VISIBLE_DISTRIBUTORS);

  // El volumen llega como string desde el backend: se formatea al idioma
  // activo para no mostrar el punto decimal inglés en español.
  const formatNumber = (value: number | string) => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed)
      ? parsed.toLocaleString(i18n.language)
      : String(value);
  };

  /* La patente abre la línea de specs: el título es ahora el nombre operativo
     y la unidad se sigue pudiendo buscar por patente sin pasar el mouse, que es
     lo único que hay —esta vista no tiene buscador. */
  const specs = [
    van.plate,
    van.brandModel,
    van.volumeM3
      ? t(`${I18N_BASE}.volume`, { value: formatNumber(van.volumeM3) })
      : null,
    van.weightKg
      ? t(`${I18N_BASE}.weight`, { value: formatNumber(van.weightKg) })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="vans-card">
      <div className="vans-card__cover">
        {van.photoUrl && !photoFailed ? (
          /* La portada recorta la foto para que todas las tarjetas midan lo
             mismo, así que la foto completa solo se ve en el visor. */
          <div
            className="vans-card__cover-photo"
            role="button"
            tabIndex={0}
            aria-label={t(`${I18N_BASE}.view_photo_of`, { plate: van.plate })}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                // El espacio scrollea la página si se lo deja pasar.
                event.preventDefault();
                setPreviewOpen(true);
              }
            }}
          >
            <Image
              src={van.photoUrl}
              alt={van.plate}
              onError={() => setPhotoFailed(true)}
              preview={{
                visible: previewOpen,
                onVisibleChange: setPreviewOpen,
                /* La misma máscara que el expediente del operario: el texto
                   sale de `common` para que no puedan divergir. */
                mask: (
                  <span className="vans-photo-mask">
                    <ZoomInOutlined /> {t('common.buttons.view_photo')}
                  </span>
                ),
              }}
            />
          </div>
        ) : (
          /* La camioneta sin foto y la que tiene una que no cargó son casos
             distintos: la URL firmada pudo caducar y decir “sin foto” sería
             incorrecto. El fallo se marca en rojo porque es un problema que
             alguien tiene que revisar, no un dato que falta. */
          <span
            className={`vans-card__cover-empty${
              photoFailed ? ' vans-card__cover-empty--error' : ''
            }`}
          >
            {photoFailed ? (
              <>
                <FileImageOutlined /> {t(`${I18N_BASE}.photo_unavailable`)}
              </>
            ) : (
              <>
                <PictureOutlined /> {t(`${I18N_BASE}.no_photo`)}
              </>
            )}
          </span>
        )}
        <span className="vans-card__status">
          {/* El color se elige por `value`; el texto mostrado es `label`. */}
          <Tag color={VAN_STATUS_COLORS[van.estado?.value] ?? 'default'}>
            {van.estado?.label}
          </Tag>
        </span>
      </div>

      <div className="vans-card__body">
        <div className="vans-card__name">{van.name || van.plate}</div>
        <div className="vans-card__specs">{specs}</div>
        <div className="vans-card__distributors">
          {visible.map((distributor) => (
            <Tag color="blue" key={distributor.id}>
              {distributor.name}
            </Tag>
          ))}
          {extra.length > 0 && (
            <Tooltip title={extra.map((d) => d.name).join(', ')}>
              <Tag style={{ cursor: 'pointer' }}>+{extra.length}</Tag>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="vans-card__crew">
        <CrewSlot
          member={van.crew?.conductor ?? null}
          role="conductor"
          emptyHint={t(`${I18N_BASE}.no_crew_hint`)}
          onClick={onCrewClick}
        />
        <CrewSlot
          member={van.crew?.peoneta ?? null}
          role="peoneta"
          emptyHint={
            van.crew?.conductor ? t(`${I18N_BASE}.no_peoneta_hint`) : undefined
          }
          onClick={onCrewClick}
        />
      </div>
    </div>
  );
}

export default VanCard;
